import { PantallaCargando } from '@/components/Cargando';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import SectionHelp from '@/components/help/SectionHelp';
import EmptyStateGuide from '@/components/help/EmptyStateGuide';
import SensitiveActionHelp from '@/components/help/SensitiveActionHelp';
import sensitiveActions from '@/help/sensitiveActions';
import { UserPlus, AlertTriangle } from 'lucide-react';

export default function CrmInboundPending() {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [converting, setConverting] = useState(false);

  const load = () => api.get('/tenant/crm/inbound/pending').then(r => setEvents(r.data?.items || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const convertOne = async (eventId) => {
    setConverting(true);
    try {
      const { data } = await api.post(`/tenant/crm/inbound/${eventId}/convert`, { create_deal: true });
      toast.success(`Lead agregado: ${data.lead?.display_name || 'OK'}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error al convertir'); }
    finally { setConverting(false); }
  };

  const convertBulk = async () => {
    if (selected.size === 0) return;
    if (selected.size > 25) { toast.error('Máximo 25 por lote'); return; }
    setConverting(true);
    try {
      const { data } = await api.post('/tenant/crm/inbound/convert-bulk', { event_ids: [...selected], create_deal: true });
      toast.success(`Convertidos: ${data.converted}, Fallidos: ${data.failed}`);
      setSelected(new Set());
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || 'Error'); }
    finally { setConverting(false); }
  };

  const toggleSelect = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };

  if (loading) return <PantallaCargando />;

  return (
    <div className="space-y-4" data-testid="crm-inbound-pending">
      <SectionHelp module="crm_convert" />

      {events.length === 0 && (
        <div className="space-y-3">
          <EmptyStateGuide module="crm_convert"
            message="No hay leads entrantes por revisar. Configura Fuentes de Leads para recibir leads, o revisa tus leads CRM."
            ctaLabel="Fuentes de Leads"
            onCta={() => navigate('/tenant/integrations/lead-sources')} />
          <div className="text-center">
            <Button size="sm" variant="outline" onClick={() => navigate('/tenant/crm/leads')}>Ver leads CRM</Button>
          </div>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
          <span className="text-sm font-medium">{selected.size} seleccionados</span>
          <SensitiveActionHelp action={sensitiveActions.crm_convert_inbound} onConfirm={convertBulk}>
            <Button size="sm" disabled={converting} data-testid="crm-bulk-convert">{converting ? 'Convirtiendo...' : 'Agregar al CRM'}</Button>
          </SensitiveActionHelp>
          <button onClick={() => setSelected(new Set())} className="text-xs text-[#64748B] underline">Deseleccionar</button>
        </div>
      )}

      {events.length > 0 && (
        <div className="space-y-2" data-testid="crm-pending-list">
          {events.map(e => (
            <div key={e.id} className="bg-white border rounded-lg px-4 py-3 flex items-center gap-3" data-testid={`pending-event-${e.id}`}>
              <input type="checkbox" checked={selected.has(e.id)} onChange={() => toggleSelect(e.id)} className="flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[#0F172A]">{e.contact_name || 'Sin nombre'}</p>
                <p className="text-xs text-[#64748B]">{e.contact_email} · {e.source_name || '—'}</p>
                <div className="flex gap-2 text-[10px] text-[#94A3B8]">
                  {e.medium && <span>Medio: {e.medium}</span>}
                  {e.channel && <span>Canal: {e.channel}</span>}
                  {e.campaign_name && <span>Campaña: {e.campaign_name}</span>}
                  {e.branch_name && <span>Sede: {e.branch_name}</span>}
                </div>
                {e.interest?.service && <p className="text-xs text-[#94A3B8]">Interés: {e.interest.service}</p>}
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                {e.consent_status === 'denied' && (
                  <span className="text-[10px] text-red-500 flex items-center gap-0.5"><AlertTriangle className="w-3 h-3" /> Sin permiso</span>
                )}
                <SensitiveActionHelp action={sensitiveActions.crm_convert_inbound} onConfirm={() => convertOne(e.id)}>
                  <button disabled={converting || e.consent_status === 'denied'}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 disabled:opacity-40"
                    data-testid={`convert-btn-${e.id}`}>
                    <UserPlus className="w-3.5 h-3.5" /> Agregar al CRM
                  </button>
                </SensitiveActionHelp>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
